import { fetchWithAuth, ApiError, readBody, getJson } from "./client";

// /api/v1/operator/count — strict pre-flight match estimation.
// Wizard's review step calls this to show "your filter matches N
// pages across these months" before submission.

export interface CountRequest {
  filter: string;
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD (exclusive)
  // Corpora to count across. Omitted ⇒ broadcast. More than one ⇒ the
  // operator unions the rollups (union_by_name) and reports the total.
  content_types?: string[];
}

export interface CountResponse {
  total: number;
  by_month: Record<string, number>;
  missing_months: string[];
}

export interface CountErrorDetail {
  message: string;
  missing_months: string[];
}

export async function countMatches(req: CountRequest): Promise<CountResponse> {
  const res = await fetchWithAuth("/api/v1/operator/count", {
    method: "POST",
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await readBody(res), `count returned ${res.status}`);
  }
  return (await res.json()) as CountResponse;
}

// --- Async (kick + poll) flavor used by the funnel breadcrumb ----------
//
// Kick is non-blocking; the SPA polls /count/{id} every second until
// the operator-side scan finishes. Lets the user keep typing in the
// filter input without each keystroke spawning a 30s blocking call.

export interface StartCountResponse {
  request_id: string;
  status: "running";
}

// Running state may carry portal-side progress info from the per-month
// fan-out. progress_total = number of months the count was split into;
// progress_completed bumps as each sub-call lands. partial_total is the
// running sum so we can show "318k so far" while the rest catches up.
export interface CountProgress {
  progress_completed?: number;
  progress_total?: number;
  partial_total?: number;
  partial_by_month?: Record<string, number>;
}

export type CountStatus =
  | ({ status: "running" } & CountProgress)
  | ({ status: "succeeded" } & CountResponse)
  | ({ status: "failed"; message: string; missing_months?: string[] } & CountProgress);

export async function startCountEstimate(
  req: CountRequest,
): Promise<StartCountResponse> {
  const res = await fetchWithAuth("/api/v1/count/start", {
    method: "POST",
    body: JSON.stringify(req),
  });
  if (res.status !== 202) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `count/start returned ${res.status}`,
    );
  }
  return (await res.json()) as StartCountResponse;
}

export function pollCountEstimate(requestId: string): Promise<CountStatus> {
  return getJson<CountStatus>(`/api/v1/count/${requestId}`);
}
